import { FC, useState } from "react";
import style from "./TeamsAndSpaces.module.scss";
import Typhography from "../../shared/Typhography/Typhography";
import { FlagBannerIcon, BellRingingIcon } from "@phosphor-icons/react";
import CustomButton from "../../shared/CustomButton/CustomButton";
import AddSpaceModal from "../../components/TeamsAndSpaces/AddSpaceModal";
import { useEndpoints } from "../../hooks/useEndpoints";
import { useTeamsAndSpaces } from "./useTeamsAndSpaces";
import { useGlobalReminder } from "./useGlobalReminder";
import CustomTextInput from "../../shared/CustomInput/CustomTextInput";
import TeamAndSpaceCard from "../../components/TeamsAndSpaces/TeamAndSpaceCard/TeamAndSpaceCard";
import { ISpace } from "../../models/Space";
import type { SpaceCreateInput } from "@leopart/shared/contracts/space.contract";
import { Skeleton } from "@chakra-ui/react";
import CustomPaginationTable from "../../shared/CustomPaginationTable/CustomPaginationTable";
import EmptyContainer from "../../shared/EmptyContainer/EmptyContainer";
import { useDirectorId } from "../../hooks/directorId";
import { MagnifyingGlassIcon, CubeTransparentIcon } from "@phosphor-icons/react";
import { ShowByRole } from "../../shared/ShowByRole";
import toast from "react-hot-toast";

export const TeamsAndSpaces: FC = () => {
  const [isOpen, setIsOpen] = useState<boolean | ISpace>();
  const onClose = () => setIsOpen(false);
  const onOpen = (value?: ISpace) => setIsOpen(value || true);
  const [isLoading, setIsLoading] = useState(false);
  const directorId = useDirectorId();

  const { postSpace, patchSpace } = useEndpoints();
  const reminder = useGlobalReminder();
  const {
    loadTeams,
    data,
    isLoading: isLoadingData,
    searchValue,
    updateSearchValue,
    pagination,
    updatePagination,
    isEmptyData,
  } = useTeamsAndSpaces();

  const onConfirmModal = async (form: SpaceCreateInput) => {
    if (!isOpen) return;
    setIsLoading(true);
    const isPost = typeof isOpen === "boolean";
    const id = toast.loading(`${isPost ? "Aggiunta" : "Modifica"} ${form.name} in corso...`);
    try {
      if (isPost) {
        await postSpace({ ...form, directorId });
      } else {
        await patchSpace((isOpen as ISpace)._id || "", { ...form });
      }
      await loadTeams();
      toast.success(`${form.name} ${isPost ? "aggiunto" : "modificato"}`, { id });
      onClose();
    } catch (error) {
      toast.error(`Errore durante l'operazione`, { id });
      // Rilancia: AddSpaceModal (rimasto aperto) mappa i 400 di validazione
      // sotto ai campi via applyServerErrors.
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const totalCount = data?.totalDocs ?? data?.docs?.length ?? 0;

  return (
    <>
      <div className={style.container}>
        {/* Header */}
        <div className={style.header}>
          <div className="d-flex align-items-center gap-3">
            <div className={style.headerIconWrapper}>
              <FlagBannerIcon size={20} />
            </div>
            <div>
              <Typhography type="h1" size="bold-24">Teams e spazi</Typhography>
              <Typhography size="regular-14" className={style.subtitle}>
                Gestisci tutti i team e gli spazi
              </Typhography>
            </div>
          </div>
          {!isLoadingData && totalCount > 0 && (
            <div className={style.countBadge}>
              <Typhography size="bold-12">{totalCount}</Typhography>
            </div>
          )}
        </div>

        {/* Toolbar */}
        {!isEmptyData.current && (
          <div className={style.toolbar}>
            <CustomTextInput
              value={searchValue || ""}
              onChange={updateSearchValue}
              rightAddon={<MagnifyingGlassIcon />}
              placeholder="Cerca per nome..."
            />
            <div className={style.actions}>
              <ShowByRole roles={["isAdmin", "isDirector", "isCoach"]}>
                <CustomButton
                  onClick={reminder.send}
                  isLoading={reminder.isSending}
                  icon={<BellRingingIcon size={16} />}
                  iconPosition="left"
                  color="outline"
                >
                  Sollecita
                </CustomButton>
              </ShowByRole>
              <ShowByRole roles={["isAdmin", "isDirector"]}>
                <CustomButton
                  onClick={() => onOpen()}
                  icon="plus"
                  iconPosition="left"
                  color="secondary"
                >
                  Aggiungi spazio
                </CustomButton>
              </ShowByRole>
            </div>
          </div>
        )}

        {/* Empty state */}
        {isEmptyData.current && !isLoadingData && (
          <EmptyContainer
            Icon={CubeTransparentIcon}
            title="Nessuno spazio presente"
            buttonText="Aggiungi spazio"
            description="Aggiungine uno per cominciare"
            onClick={() => onOpen()}
          />
        )}

        {/* Loading skeletons */}
        {isLoadingData && (
          <div className={style.grid}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} height="90px" borderRadius="12px" />
            ))}
          </div>
        )}

        {/* Cards grid */}
        {!isLoadingData && !isEmptyData.current && (
          <>
            <div className={style.grid}>
              {data?.docs.map((team) => (
                <TeamAndSpaceCard
                  key={team._id}
                  onClickEditButton={() => onOpen(team)}
                  data={team}
                />
              ))}
            </div>

            {data && (
              <div className={style.footer}>
                <select
                  className={style.limitSelect}
                  value={pagination.limit}
                  onChange={({ target: { value } }) => updatePagination("limit", +value)}
                >
                  <option value="20">20 per pagina</option>
                  <option value="50">50 per pagina</option>
                  <option value="100">100 per pagina</option>
                </select>
                <CustomPaginationTable
                  data={data}
                  onChange={(value) => updatePagination("page", value)}
                />
              </div>
            )}
          </>
        )}
      </div>

      <AddSpaceModal
        isLoading={isLoading}
        isOpen={isOpen}
        onClose={onClose}
        onDelete={() => { onClose(); loadTeams(); }}
        onConfirm={onConfirmModal}
      />
    </>
  );
};
